import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isLineMemberBadgeVisible,
  NO_MEMBER_STATE,
  type ApprovalLineMemberKey,
  type ApprovalLineMemberState,
} from '@/utils/approval/approvalLineMemberStatus';
import ApprovalLineCard from './ApprovalLineCard';
import AuthorCard from './AuthorCard';
import { groupApprovers } from './statusMapping';
import { ApprovalLineRow, ApprovalLineSectionLabel } from './styles';
import type {
  ApprovalViewApprover,
  ApprovalViewAuthor,
  ApprovalViewReader,
  DocumentStatus,
} from './types';

/**
 * 결재라인 영역 — groupApprovers 로 단일/협조결재 분리 렌더.
 * 작성자 카드 prepend(v1/v2 호환): v2 는 approvers 에 작성자가 없어 AuthorCard prepend,
 * v1 품의는 첫 결재자=작성자라 중복이므로 prepend 생략(첫 결재자 카드가 겸함).
 */

type ApprovalLineSlotProps = {
  approvers: ApprovalViewApprover[];
  author?: ApprovalViewAuthor; // optional. 결재라인 row 맨 앞 "작성자" 카드용. v2 응답 author 객체.
  authoredAt?: string | null; // 문서 상신일. 작성자 카드 하단 chip 날짜. 임시저장은 값 없음
  readers?: ApprovalViewReader[]; // readers (이 문서를 읽은 사람들). 눈 아이콘 prepend
  currentApproverOrder?: number;
  documentStatus?: DocumentStatus; // 문서 status. (P→info / H→warning / R·C·S→ 강조 없음)
  /**
   * 결재라인 인물의 현재 퇴사·휴직 상태 조회 (useApprovalLineMembers).
   * 겸직 때문에 intranetId 만으로는 사람이 갈리지 않아 인물 객체째 넘긴다.
   */
  getMemberState?: (person: ApprovalLineMemberKey) => ApprovalLineMemberState;
};

const ApprovalLineSlot = ({
  approvers,
  author,
  authoredAt,
  readers,
  currentApproverOrder,
  documentStatus,
  getMemberState,
}: ApprovalLineSlotProps) => {
  const { t } = useTranslation();
  const groups = useMemo(() => groupApprovers(approvers), [approvers]);
  const viewedSets = useMemo(() => {
    const passportIds = new Set<string>();
    const intranetIds = new Set<string>();
    readers?.forEach((r) => {
      if (r.passportId) passportIds.add(r.passportId);
      if (r.intranetId) intranetIds.add(r.intranetId);
    });
    if (author?.passportId) passportIds.delete(author.passportId);
    if (author?.intranetId) intranetIds.delete(author.intranetId);
    return { passportIds, intranetIds };
  }, [readers, author]);

  const isApproverViewed = (a: ApprovalViewApprover) =>
    (!!a.passportId && viewedSets.passportIds.has(a.passportId)) ||
    (!!a.intranetId && viewedSets.intranetIds.has(a.intranetId));

  // 완료·반려·취소 문서는 퇴사·휴직 모두 숨김 — 퇴사만 빼면 그 자리에 휴직 배지가 새로 튐.
  const showMemberBadges = isLineMemberBadgeVisible(documentStatus);
  const memberState = (person: ApprovalLineMemberKey) =>
    showMemberBadges
      ? getMemberState?.(person) ?? NO_MEMBER_STATE
      : NO_MEMBER_STATE;

  const authorState = author ? memberState(author) : NO_MEMBER_STATE;

  if (approvers.length === 0) return null;

  // groups 는 groupApprovers 에서 order 정렬을 마친 상태 — filter 가 상대 순서를 보존하므로
  // 단일/협조 양쪽 모두 여기서 다시 정렬하지 않는다(정렬 기준 단일화).
  const singleGroups = groups.filter(
    (g): g is Extract<typeof g, { kind: 'single' }> => g.kind === 'single',
  );
  const coopGroups = groups.filter(
    (g): g is Extract<typeof g, { kind: 'cooperation' }> =>
      g.kind === 'cooperation',
  );

  const isAuthorFirstApprover =
    !!author && singleGroups[0]?.approver.passportId === author.passportId;
  const showAuthorCard = !!author && !isAuthorFirstApprover;

  return (
    <>
      <ApprovalLineSectionLabel>{t('결재라인')}</ApprovalLineSectionLabel>
      <ApprovalLineRow noBorder={coopGroups.length > 0}>
        {showAuthorCard && author && (
          <AuthorCard
            author={author}
            authoredAt={authoredAt}
            isDeactivated={authorState.deactivated}
            isOnLeave={authorState.onLeave}
          />
        )}
        {singleGroups.map((group, gIdx) => {
          const isCurrent = group.approver.order === currentApproverOrder;
          const state = memberState(group.approver);
          return (
            <ApprovalLineCard
              key={`single-${group.approver.passportId}-${group.approver.order}-${gIdx}`}
              approver={group.approver}
              isCurrent={isCurrent}
              documentStatus={documentStatus}
              orderLabel={group.approver.order}
              hasViewed={isApproverViewed(group.approver)}
              isDeactivated={state.deactivated}
              isOnLeave={state.onLeave}
            />
          );
        })}
      </ApprovalLineRow>
      {coopGroups.length > 0 && (
        <>
          <ApprovalLineSectionLabel>
            {t('협조결재 라인')}
          </ApprovalLineSectionLabel>
          <ApprovalLineRow>
            {coopGroups.flatMap((group) =>
              group.members.map((member, mIdx) => {
                const isCurrent = member.order === currentApproverOrder;
                const state = memberState(member);
                return (
                  <ApprovalLineCard
                    key={`coop-m-${member.passportId}-${member.order}-${mIdx}`}
                    approver={member}
                    isCurrent={isCurrent}
                    documentStatus={documentStatus}
                    orderLabel={member.order}
                    isCooperation
                    hasViewed={isApproverViewed(member)}
                    isDeactivated={state.deactivated}
                    isOnLeave={state.onLeave}
                  />
                );
              }),
            )}
          </ApprovalLineRow>
        </>
      )}
    </>
  );
};

export default ApprovalLineSlot;
